import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle, Image as ImageIcon, ClipboardPaste, ArrowLeft, Upload,
  ShieldAlert, Flame, MessageSquareReply, Sparkles, Trash2, ChevronRight, Smartphone,
  ChevronUp, ChevronDown, RefreshCw, Anchor, HeartHandshake,
} from "lucide-react";
import Layout from "@/components/Layout";
import { ResultView } from "@/components/Results";
import PaywallModal from "@/components/PaywallModal";
import { api, vibrate } from "@/lib/api";
import { celebrate } from "@/lib/feedback";
import { useAuth } from "@/context/AuthContext";

const VERTICALS = [
  { id: "red_flags", label: "Red Flags", icon: ShieldAlert },
  { id: "rizz_score", label: "Rizz Score", icon: Flame },
  { id: "reply_suggestions", label: "Réponses", icon: MessageSquareReply },
  { id: "attachment_style", label: "Attachement", icon: Anchor },
  { id: "compatibility", label: "Compatibilité", icon: HeartHandshake },
];

const METHODS = [
  {
    id: "wa-file", icon: MessageCircle, title: "J'ai une conv WhatsApp",
    desc: "Export .txt ou .zip — précision maximale.", badge: "Meilleure précision",
    color: "from-emerald-500/20 to-emerald-500/5", ring: "text-emerald-400",
  },
  {
    id: "screenshots", icon: ImageIcon, title: "J'ai des screenshots",
    desc: "Tinder, Hinge, Insta… jusqu'à 20 images.",
    color: "from-pink-500/20 to-purple-500/5", ring: "text-pink-400",
  },
];

const SAMPLE = `moi: salut ça va ? t'as passé un bon week-end ?
lui: ouais tranquille et toi
moi: cool ! on se voit quand du coup ?
lui: jsp on verra
moi: ok dis moi quand t'es dispo
lui: t'es trop dispo toi non ? faut te calmer un peu`;

export default function Import() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState("choose"); // choose | wa-file | screenshots | paste | review | result
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [me, setMe] = useState(null);
  const [msgs, setMsgs] = useState([]); // [{text, mine}]
  const [meta, setMeta] = useState({ total: 0, truncated: false });
  const [win, setWin] = useState(null);
  const [source, setSource] = useState("paste");
  const [pasteText, setPasteText] = useState("");
  const [vertical, setVertical] = useState("red_flags");
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(null);

  const reset = () => {
    setStep("choose"); setParticipants([]); setMe(null); setMsgs([]);
    setPasteText(""); setResult(null); setVertical("red_flags");
    setMeta({ total: 0, truncated: false }); setWin(null);
  };

  const mapMsgs = (arr, meVal) =>
    (arr || []).map((m) => ({ text: m.text, mine: m.sender === meVal, _sender: m.sender }));

  const invertAll = () => {
    vibrate();
    setMsgs((prev) => prev.map((m) => ({ ...m, mine: !m.mine })));
    if (participants.length === 2) {
      const other = participants.find((p) => p !== me);
      if (other) setMe(other);
    }
  };

  const loadWindow = async (offset) => {
    if (!win) return;
    vibrate(); setLoading(true);
    try {
      const res = await api.post("/import/window", { import_id: win.importId, offset });
      const data = res.data;
      setWin((w) => ({
        ...w, offset: data.offset, hasOlder: data.has_older, hasNewer: data.has_newer,
        dateFrom: data.date_from, dateTo: data.date_to, rangeFrom: data.range_from, rangeTo: data.range_to,
      }));
      setMsgs(mapMsgs(data.messages, me));
    } catch (e) {
      toast.error("Chargement impossible");
    } finally { setLoading(false); }
  };

  const buildReview = (data, src) => {
    setSource(src);
    const parts = data.participants || [];
    setParticipants(parts);
    if (parts.length && parts[0] === "me") {
      // screenshots: sender already me/them
      setMe("me");
      setMsgs((data.messages || []).map((m) => ({ text: m.text, mine: m.sender === "me" })));
    } else {
      const defaultMe = parts[0] || null;
      setMe(defaultMe);
      setMsgs((data.messages || []).map((m) => ({ text: m.text, mine: m.sender === defaultMe })));
    }
    setStep("review");
  };

  const onPickMe = (p) => {
    vibrate();
    setMe(p);
    // recompute mine based on original participant — we only have text now, so keep a shadow
    setMsgs((prev) => prev.map((m) => ({ ...m, mine: m._sender ? m._sender === p : m.mine })));
  };

  const uploadWaFile = async (file) => {
    if (!file) return;
    vibrate(); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/whatsapp-file", fd);
      const data = res.data;
      const parts = data.participants || [];
      const defaultMe = parts[0] || null;
      setParticipants(parts);
      setMe(defaultMe);
      setSource("whatsapp");
      setMeta({ total: data.total || (data.messages || []).length, truncated: !!data.truncated });
      setWin({
        importId: data.import_id, offset: data.offset, hasOlder: data.has_older, hasNewer: data.has_newer,
        dateFrom: data.date_from, dateTo: data.date_to, rangeFrom: data.range_from, rangeTo: data.range_to,
        total: data.total, stored: data.stored,
      });
      setMsgs(mapMsgs(data.messages, defaultMe));
      setStep("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible");
    } finally { setLoading(false); }
  };

  const uploadScreenshots = async (files) => {
    if (!files?.length) return;
    vibrate(); setLoading(true);
    try {
      const fd = new FormData();
      Array.from(files).slice(0, 20).forEach((f) => fd.append("files", f));
      const res = await api.post("/import/screenshots", fd);
      buildReview(res.data, "screenshot");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OCR impossible");
    } finally { setLoading(false); }
  };

  const submitPaste = async () => {
    if (!pasteText.trim()) { toast.error("Colle une conversation"); return; }
    vibrate(); setLoading(true);
    try {
      const res = await api.post("/import/paste", { raw_text: pasteText });
      const data = res.data;
      const parts = data.participants || [];
      const defaultMe = parts.find((p) => /^(moi|me)$/i.test(p)) || parts[0] || null;
      setParticipants(parts);
      setMe(defaultMe);
      setSource("paste");
      setMeta({ total: data.total || (data.messages || []).length, truncated: !!data.truncated });
      setMsgs((data.messages || []).map((m) => ({ text: m.text, mine: m.sender === defaultMe, _sender: m.sender })));
      setStep("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Format non reconnu");
    } finally { setLoading(false); }
  };

  const runAnalyze = async () => {
    const kept = msgs.filter((m) => m.text.trim());
    if (kept.length < 2) { toast.error("Il faut au moins 2 messages"); return; }
    vibrate(); setLoading(true); setResult(null);
    const contact = participants.find((p) => p !== me && p !== "me") || "lui";
    let raw_text = kept.map((m) => `${m.mine ? "moi" : contact}: ${m.text}`).join("\n");
    let contactName = contact !== "lui" ? contact : null;
    // For file imports, analyse the FULL stored history (up to 5000 msgs), not just the visible window.
    if (win?.importId) {
      try {
        const full = await api.post("/import/full-text", { import_id: win.importId, me, contact_name: contactName });
        if (full.data.raw_text) { raw_text = full.data.raw_text; contactName = full.data.contact ?? contactName; }
      } catch (e) { /* fallback to window text */ }
    }
    try {
      const res = await api.post("/analyze", {
        raw_text, vertical, source,
        contact_name: contactName,
      });
      celebrate();
      setResult({ vertical: res.data.vertical, data: res.data.result });
      setStep("result");
      checkAuth();
    } catch (e) {
      if (e?.response?.status === 402) {
        const detail = e.response.data?.detail;
        if (detail && typeof detail === "object" && detail.error === "quota_exceeded") {
          setPaywall(detail);
        } else {
          toast.error("Analyses gratuites épuisées");
          navigate("/pricing");
        }
      } else {
        toast.error("Analyse échouée");
      }
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <AnimatePresence mode="wait">
        {step === "choose" && (
          <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-5">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Nouvelle analyse</p>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1.5">Importe ta <span className="text-gradient">conv</span></h1>
              <p className="text-slate-400 text-sm mt-2">Choisis ta méthode. Le Doc s'occupe du reste.</p>
            </div>
            {METHODS.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  data-testid={`method-${m.id}`}
                  onClick={() => { vibrate(); setStep(m.id); }}
                  className={`w-full text-left rounded-2xl bg-gradient-to-br ${m.color} border border-white/10 p-5 hover:border-white/25 transition-colors active:scale-[0.99]`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-zinc-950/60 flex items-center justify-center ${m.ring}`}>
                      <Icon size={22} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{m.title}</p>
                        {m.badge && (
                          <span className="text-[9px] font-bold tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                            {m.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-400 mt-0.5">{m.desc}</p>
                    </div>
                    <ChevronRight className="text-zinc-600" size={18} />
                  </div>
                </button>
              );
            })}
            <button
              data-testid="method-paste"
              onClick={() => { vibrate(); setStep("paste"); }}
              className="w-full flex items-center justify-center gap-2 text-sm text-zinc-400 hover:text-pink-400 transition-colors py-2"
            >
              <ClipboardPaste size={15} /> Ou colle simplement le texte
            </button>
            <button
              data-testid="method-whatsapp-live"
              onClick={() => { vibrate(); navigate("/whatsapp"); }}
              className="w-full flex items-center justify-center gap-2 text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              <Smartphone size={13} /> Connecter WhatsApp en direct (bientôt)
            </button>
          </motion.div>
        )}

        {step === "wa-file" && (
          <StepShell key="wa" title="Import WhatsApp" onBack={reset}>
            <div className="rounded-2xl glass-card p-5 space-y-3 text-sm text-zinc-400">
              <p className="text-zinc-200 font-semibold">Comment exporter :</p>
              <p><span className="text-pink-400 font-semibold">iPhone :</span> ouvre la conv → nom du contact en haut → Exporter la discussion → Sans les médias.</p>
              <p><span className="text-pink-400 font-semibold">Android :</span> ⋮ → Plus → Exporter la discussion → Sans les médias.</p>
              <p className="text-xs text-zinc-500">Le fichier .txt (ou .zip) arrive par mail/partage. Récupère-le puis dépose-le ici.</p>
            </div>
            <label
              data-testid="wa-file-drop"
              className="mt-4 block rounded-2xl border-2 border-dashed border-zinc-700 hover:border-pink-500 transition-colors p-8 text-center cursor-pointer"
            >
              <Upload className="mx-auto text-zinc-500 mb-2" size={26} />
              <p className="text-sm text-zinc-300 font-semibold">Déposer le fichier</p>
              <p className="text-xs text-zinc-600 mt-1">.txt ou .zip</p>
              <input
                data-testid="wa-file-input"
                type="file" accept=".txt,.zip" className="hidden"
                onChange={(e) => uploadWaFile(e.target.files?.[0])}
              />
            </label>
            {loading && <p className="text-center text-sm text-pink-400 mt-4">Lecture du fichier…</p>}
          </StepShell>
        )}

        {step === "screenshots" && (
          <StepShell key="ss" title="Import screenshots" onBack={reset}>
            <div className="rounded-2xl glass-card p-5 text-sm text-zinc-400">
              Ajoute tes captures dans l'ordre (haut → bas). L'IA lit chaque message.
              Jusqu'à 20 images. Tu pourras corriger avant l'analyse.
            </div>
            <label
              data-testid="ss-drop"
              className="mt-4 block rounded-2xl border-2 border-dashed border-zinc-700 hover:border-pink-500 transition-colors p-8 text-center cursor-pointer"
            >
              <ImageIcon className="mx-auto text-zinc-500 mb-2" size={26} />
              <p className="text-sm text-zinc-300 font-semibold">Ajouter des captures</p>
              <p className="text-xs text-zinc-600 mt-1">JPG, PNG, HEIC</p>
              <input
                data-testid="ss-input"
                type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => uploadScreenshots(e.target.files)}
              />
            </label>
            {loading && (
              <div className="text-center mt-5">
                <div className="mx-auto w-8 h-8 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" />
                <p className="text-sm text-pink-400 mt-3">L'IA lit tes captures…</p>
              </div>
            )}
          </StepShell>
        )}

        {step === "paste" && (
          <StepShell key="paste" title="Coller le texte" onBack={reset}>
            <div className="neon-border rounded-xl">
              <textarea
                data-testid="paste-input"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"Colle ta conversation ici…\nmoi: coucou\nlui: salut"}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-4 text-sm text-white focus:outline-none resize-none min-h-[180px]"
              />
            </div>
            <button
              data-testid="paste-sample"
              onClick={() => setPasteText(SAMPLE)}
              className="text-xs text-zinc-500 hover:text-pink-400 mt-2"
            >
              Charger un exemple
            </button>
            <button
              data-testid="paste-continue"
              onClick={submitPaste}
              disabled={loading}
              className="mt-4 w-full rounded-full bg-pink-500 text-white font-bold py-4 hover:bg-pink-600 transition-colors glow-pink disabled:opacity-60"
            >
              {loading ? "Lecture…" : "Continuer"}
            </button>
          </StepShell>
        )}

        {step === "review" && (
          <StepShell key="review" title="Vérifie & analyse" onBack={reset}>
            {win ? (
              <div data-testid="window-nav" className="mb-4 rounded-xl glass-card p-3">
                <p className="text-xs text-zinc-400">
                  Messages <span className="text-zinc-200 font-semibold">{win.rangeFrom}–{win.rangeTo}</span> sur {win.total}
                  {win.dateFrom && <span className="text-zinc-500"> · du {win.dateFrom} au {win.dateTo}</span>}
                </p>
                <div className="flex gap-2 mt-2">
                  <button
                    data-testid="window-older"
                    disabled={!win.hasOlder || loading}
                    onClick={() => loadWindow(win.offset + 250)}
                    className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-zinc-800 text-xs font-semibold py-2 disabled:opacity-40 hover:bg-zinc-700 transition-colors"
                  >
                    <ChevronUp size={14} /> 250 plus anciens
                  </button>
                  <button
                    data-testid="window-newer"
                    disabled={!win.hasNewer || loading}
                    onClick={() => loadWindow(win.offset - 250)}
                    className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-zinc-800 text-xs font-semibold py-2 disabled:opacity-40 hover:bg-zinc-700 transition-colors"
                  >
                    <ChevronDown size={14} /> 250 plus récents
                  </button>
                </div>
                <p className="text-[11px] text-emerald-400/90 mt-2">
                  ✓ L'analyse porte sur les <span className="font-bold">{win.stored} messages</span>{win.total > win.stored && ` (les plus récents sur ${win.total})`} — cette fenêtre sert juste à vérifier les côtés.
                </p>
              </div>
            ) : meta.truncated && (
              <div data-testid="truncated-banner" className="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 p-3 text-xs text-amber-300">
                Conversation longue ({meta.total} messages). On garde les <span className="font-bold">250 plus récents</span> — c'est ce qui compte pour l'analyse.
              </div>
            )}
            {(() => {
              const contactLabel = participants.find((p) => p !== me && p !== "me") || "lui";
              return (
            <>
            <div className="mb-4 rounded-xl glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Qui es-tu ?</p>
                <button
                  data-testid="invert-all"
                  onClick={invertAll}
                  className="flex items-center gap-1 text-xs font-semibold text-pink-400 hover:text-pink-300"
                >
                  <RefreshCw size={13} /> Inverser
                </button>
              </div>
              {participants.length > 1 && participants[0] !== "me" ? (
                <div className="flex gap-2 flex-wrap">
                  {participants.map((p) => (
                    <button
                      key={p}
                      data-testid={`me-pick-${p}`}
                      onClick={() => onPickMe(p)}
                      className={`px-4 py-2 rounded-full text-sm border transition-all ${
                        me === p ? "border-pink-500 bg-pink-500/10 text-pink-400 font-semibold" : "border-zinc-700 text-zinc-400"
                      }`}
                    >
                      {me === p ? "✓ " : ""}{p}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-500">Tes messages sont à droite (rose). Utilise « Inverser » si c'est le contraire.</p>
              )}
            </div>

            <p className="text-xs text-zinc-500 mb-2">
              Vérifie les côtés : <span className="text-pink-400 font-semibold">toi = à droite</span>. Tape la bulle pour changer, ou édite le texte.
            </p>
            <div data-testid="review-list" className="space-y-1.5 max-h-[38vh] overflow-y-auto pr-1">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className={`group flex items-end gap-1 max-w-[88%] ${m.mine ? "flex-row-reverse" : ""}`}>
                    <div
                      className={`rounded-2xl px-3 py-1.5 ${
                        m.mine ? "bg-pink-500/20 border border-pink-500/30" : "bg-zinc-800 border border-zinc-700"
                      }`}
                    >
                      <button
                        onClick={() => setMsgs((prev) => prev.map((x, j) => j === i ? { ...x, mine: !x.mine } : x))}
                        className={`block text-[9px] font-bold uppercase tracking-wide mb-0.5 ${m.mine ? "text-pink-300" : "text-zinc-400"}`}
                      >
                        {m.mine ? "moi" : contactLabel}
                      </button>
                      <input
                        value={m.text}
                        onChange={(e) => setMsgs((prev) => prev.map((x, j) => j === i ? { ...x, text: e.target.value } : x))}
                        className="bg-transparent text-sm text-white focus:outline-none w-full min-w-[140px]"
                      />
                    </div>
                    <button
                      onClick={() => setMsgs((prev) => prev.filter((_, j) => j !== i))}
                      className="opacity-40 group-hover:opacity-100 text-zinc-600 hover:text-red-400 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            </>
              );
            })()}

            <div className="grid grid-cols-3 gap-2 mt-5">
              {VERTICALS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  data-testid={`review-vertical-${id}`}
                  onClick={() => { vibrate(); setVertical(id); }}
                  className={`flex flex-col items-center gap-1 rounded-xl py-3 border transition-all ${
                    vertical === id ? "border-pink-500 bg-pink-500/10 text-pink-400" : "border-zinc-800 bg-zinc-900 text-zinc-400"
                  }`}
                >
                  <Icon size={16} />
                  <span className="text-[11px] font-semibold text-center leading-tight">{label}</span>
                </button>
              ))}
            </div>

            <button
              data-testid="review-analyze"
              onClick={runAnalyze}
              disabled={loading}
              className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-pink-500 text-white font-bold py-4 hover:bg-pink-600 transition-colors glow-pink disabled:opacity-60"
            >
              {loading ? (
                <><span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Le Doc analyse…</>
              ) : (
                <><Sparkles size={18} /> Analyser ({win?.importId ? win.stored : msgs.filter((m) => m.text.trim()).length} messages)</>
              )}
            </button>
          </StepShell>
        )}

        {step === "result" && result && (
          <StepShell key="result" title="Résultat" onBack={reset} backLabel="Nouvelle analyse">
            <div data-testid="analysis-result">
              <ResultView vertical={result.vertical} result={result.data} />
            </div>
          </StepShell>
        )}
      </AnimatePresence>
      {paywall && <PaywallModal data={paywall} onClose={() => setPaywall(null)} />}
    </Layout>
  );
}

function StepShell({ title, onBack, backLabel = "Retour", children }) {
  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="pb-28">
      <button
        data-testid="step-back"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4"
      >
        <ArrowLeft size={16} /> {backLabel}
      </button>
      <h1 className="font-display text-2xl font-extrabold tracking-tight mb-4">{title}</h1>
      {children}
    </motion.div>
  );
}
